// LIBRERIE/IMPORTS
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBath, faBed, faExpand, faLocationCrosshairs } from "@fortawesome/free-solid-svg-icons";
import { Link } from 'react-router-dom';
import he from "he";

// COMPONENTI
import NotFoundProperty from '../Error/NotFoundProperty';

// CONFIGURAZIONI & TIPI
import { Property } from '../../types/property';
import appConfig from '../../config/appConfig';


interface ImmobiliGridProps {
    properties: Property[];
    loading: boolean;
    error: any;
    basePath: string;
    labelSold: boolean;
}

const ImmobiliGrid: React.FC<ImmobiliGridProps> = ({ properties, loading, error, basePath, labelSold }) => {
    const { t, i18n } = useTranslation();

    if (!loading && !error && properties.length === 0) {
        return <NotFoundProperty />;
    }

    const getPropertyName = (property: Property) => {
        return `${property.tipologyDetails.name}${t('property.inAddress', ' a ')}${property.addressDetails.quartiereName || property.addressDetails.frazioneName || property.addressDetails.comuneName}`;
    };

    const langPrefix = appConfig.integrations.multilang ? `/${i18n.language}` : '';

    const negotiableText = t('common.negotiable');

    return (
        <>
            {properties.map((property: Property) => {
                const propertyName = getPropertyName(property);
                const propertyUrl = `${langPrefix}/${basePath}/${property.id}`;
                const locationName = property.addressDetails.quartiereName ?? property.addressDetails.frazioneName ?? property.addressDetails.comuneName ?? t('common.locationUnknown', 'Località sconosciuta');
                const propertyTitle = `${property.tipologyDetails.name} in ${property.contractDetails.name} ${locationName == 'Centro' || locationName == 'Semicentro' ? 'in' : 'a'} ${locationName}`;
                //const vani = property.premises ?? 0;
                const roomCount = property.rooms ?? 0;
                return (

                    <div className="col-md-6 col-lg-4" key={property.id}>

                        <div className="property-item">

                            <Link to={propertyUrl}>

                                <div className="property-image bg-overlay-gradient-04">

                                    <img
                                        className="img-fluid"
                                        src={property.mainImage}
                                        alt={t('property.card.imageAltBase', 'Immagine proprietà') + ` ${propertyName}`}
                                    />

                                    {labelSold ? (
                                        <div className="property-lable">
                                            <span className="badge badge-md badge-successi">
                                                VENDUTO DA NOI
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="property-lable">
                                            {property.contractDetails?.name && (
                                                <span className="badge badge-md badge-primary">
                                                    {t(`property.contractTypes.${property.contractDetails.name}`, property.contractDetails.name)}
                                                </span>
                                            )}
                                            {property.auctionDetails?.auction && (
                                                <span className="badge badge-md badge-primary ml-2">
                                                    {t('property.auction')}
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    <div className="property-lable-bottom-left"></div>

                                    {property.exclusive && (
                                        <div className="property-esclusiva-box">
                                            <span className="property-esclusiva">{t('property.exclusive', 'Esclusiva')}</span>
                                        </div>
                                    )}

                                    <div className="property-price">
                                        {property.priceDetails.reservedNegotiation
                                            ? t('property.priceReserved')
                                            : property.auctionDetails?.auction
                                                ? `da € ${new Intl.NumberFormat("it-IT").format(property.priceDetails.price)}`
                                                : `€ ${new Intl.NumberFormat("it-IT").format(property.priceDetails.price)}${property.priceDetails.negotiable ? ` ${negotiableText}` : ""}`
                                        }
                                    </div>
                                </div>
                                <div className="property-details">
                                    <div className="property-details-inner">

                                        <h5 className="property-title d-none">
                                            {property.tipologyDetails.name}
                                        </h5>
                                        <p className="property-title my-2">{propertyTitle}</p>

                                        <p className="property-address mb-2">
                                            <FontAwesomeIcon icon={faLocationCrosshairs} className="mr-2"/>
                                            {property.addressDetails.frazioneName || property.addressDetails.quartiereName ?
                                                `${property.addressDetails.frazioneName || property.addressDetails.quartiereName}, ${property.addressDetails.comuneName} (${property.addressDetails.provinciaSigla ?? ""})`
                                                : property.addressDetails.comuneName ?
                                                    `${property.addressDetails.comuneName} (${property.addressDetails.provinciaSigla ?? ""})`
                                                    : property.addressDetails.provinciaName ?? ''
                                            }
                                        </p>

                                        <ul className="property-info list-unstyled d-flex">
                                            {roomCount > 0 && (
                                                <li className="property-bed" title={`${roomCount} ${t('common.room', { count: roomCount })}`}>
                                                    <i>
                                                        <FontAwesomeIcon icon={faBed} />
                                                    </i>
                                                    <span>{property.rooms}</span>
                                                    {' '}
                                                    {property.rooms === 1 ? (t('common.room') || "") : (t('common.rooms') || "")}
                                                </li>
                                            )}
                                            {property.bathrooms != null && property.bathrooms !== 0 ? (
                                                <li>
                                                    <i>
                                                        <FontAwesomeIcon icon={faBath} />
                                                    </i>
                                                    <span>{property.bathrooms}</span> {property.bathrooms === 1 ? t('common.bathroom', 'Bagno') : t('common.bathrooms', 'Bagni')}
                                                </li>
                                            ) : <li aria-hidden="true" style={{ display: 'none' }} />}
                                            {property.sqmt && property.sqmt !== 0 ? (
                                                <li>
                                                    <i>
                                                        <FontAwesomeIcon icon={faExpand} />
                                                    </i>
                                                    <span>{property.sqmt} m²</span>
                                                </li>
                                            ) : <li aria-hidden="true" style={{ display: 'none' }} />}
                                        </ul>
                                        <p className="property-desc mb-2" dangerouslySetInnerHTML={{ __html: he.decode(property.descriptions?.[0].text.substring(0, 150) + "...") }} />
                                    </div>
                                    <ul className="property-listing-actions list-unstyled mb-0">
                                        <li className="property-rif">
                                            {t('property.reference', 'Rif.')}: {property.ref}
                                        </li>
                                    </ul>
                                </div>
                            </Link>
                        </div>
                    </div>
                );
            })}
        </>
    );
};

export default ImmobiliGrid;